// Ideas' Suggestions (features/recommendations.md, "Whoever added it"): what it lists, in what order, and
// what each row says about where it came from. Plain functions with type-only imports of the server's
// types, so the server-side tests can use them too.
import { waitsOnRating } from "../../src/waits.ts";
import type { Item, Person } from "../../src/web/types.ts";
import { FORMER_MEMBER, faceNames, firstName } from "./faces.ts";
import { KINDS, kindOf, originLabel, recommenders } from "./format.ts";

const CONSIDERED: ReadonlySet<string> = new Set(["idea", "option"]);

// Whether Ideas' Suggestions lists it: a place or goal still being considered (an idea, or an option that
// isn't under a choice), whoever added it, an AI on its own or someone in the trip, but not a friend's
// pick, which From friends lists as that friend's. `choiceIds`: the trip's choices; their options are
// compared on Bookings.
// Wider than `isSuggestion` (src/waits.ts), an AI's suggestion only, which stays as it is for what is about
// an AI putting something forward that nobody asked for: the map's quieter pin and get_trip's
// `waiting.suggestions_unrated_by_you`. Listing only those left a place someone in the trip added
// themselves waiting on my rating in Waiting for you, by kind, and in no list on Ideas (2026-10-23).
export function listedInSuggestions(
  item: Pick<Item, "type" | "status" | "parent" | "recommended_by">,
  choiceIds: ReadonlySet<string>,
): boolean {
  return (
    (item.type === "place" || item.type === "goal") &&
    CONSIDERED.has(item.status) &&
    !(item.parent && choiceIds.has(item.parent)) &&
    recommenders(item).length === 0
  );
}

// Where a row in Suggestions came from: "Suggested by Claude" for an AI's own suggestion, else whose idea
// it is, by the name People shows under their face: "Your idea", "Mei's idea" ("Ana H.'s idea" where two
// share a first name). Someone who has left the trip is named as they were, by first name (in full when
// that's someone else's name here now); a person whose account was deleted is "A former member's idea".
export function suggestionLine(item: Pick<Item, "origin" | "created">, members: Person[], you: string): string {
  const ai = originLabel(item);
  if (ai) return ai;
  const by = item.created.by;
  if (by.id === you) return "Your idea";
  const names = faceNames(members, you);
  const shown = names.get(by.id);
  if (shown) return `${shown}'s idea`;
  if (by.name === FORMER_MEMBER) return "A former member's idea";
  const first = firstName(by.name);
  const taken = [...names.values()].some((n) => n.toLowerCase() === first.toLowerCase());
  return `${taken ? by.name.trim() : first}'s idea`;
}

// How much the group wants it: yeses count most, then how many want it at all. Only people still in
// the trip have a say.
function strength(item: Pick<Item, "interest">, members: Person[]): [number, number] {
  const ids = new Set(members.map((m) => m.id));
  const said = item.interest.filter((x) => ids.has(x.person) && x.tier && x.tier !== "no");
  return [said.filter((x) => x.tier === "yes").length, said.length];
}

// Suggestions by kind, in the map's order of kinds: in each, the ones I haven't rated first (in the
// order they came), then the rest by how much the group wants them.
export function suggestionsByKind(
  items: Item[],
  members: Person[],
  you: string,
): { kind: (typeof KINDS)[number]; items: Item[] }[] {
  const choiceIds = new Set(items.filter((i) => i.type === "choice").map((i) => i.id));
  const listed = items.filter((i) => listedInSuggestions(i, choiceIds));
  const rank = (item: Item) => {
    const [yeses, wanting] = strength(item, members);
    return waitsOnRating(item, you) ? [1, 0, 0] : [0, yeses, wanting];
  };
  return KINDS.flatMap((kind) => {
    const list = listed
      .filter((i) => kindOf(i) === kind.key)
      .map((item, at) => ({ item, at, rank: rank(item) }))
      .sort((a, b) => {
        for (let k = 0; k < 3; k++) {
          const d = (b.rank[k] ?? 0) - (a.rank[k] ?? 0);
          if (d) return d;
        }
        return a.at - b.at;
      })
      .map((x) => x.item);
    return list.length ? [{ kind, items: list }] : [];
  });
}
